import { ReactNode, useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  Link,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate, useParams } from "react-router-dom";

import PipelineService, { pipelineErrorMessage } from "../../services/pipeline.service";
import { IPipelineStatus } from "../../types/api/pipeline.type";
import { overallState } from "./common";
import ServicesTab from "./ServicesTab";
import MaintenanceTab from "./MaintenanceTab";
import RuleCacheTab from "./RuleCacheTab";
import PagerDutyTab from "./PagerDutyTab";

const Summary = ({ label, value }: { label: string; value: ReactNode }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" component="div">
      {value}
    </Typography>
  </Grid>
);

const PipelineDetails = () => {
  const { envId = "" } = useParams();
  const navigate = useNavigate();
  const [pageTab, setPageTab] = useState(0);
  const [tenantName, setTenantName] = useState<string>(envId);
  const [status, setStatus] = useState<IPipelineStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await PipelineService.getStatus(envId);
      setStatus(data);
      setError(null);
    } catch (e) {
      setStatus(null);
      setError(pipelineErrorMessage(e, "This pipeline is unreachable"));
    } finally {
      setLoading(false);
    }
  }, [envId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // The tenant name only lives on the pipeline's Kubernetes Service, so it comes
  // from the same list the overview uses; the env id stands in until then.
  useEffect(() => {
    PipelineService.listPipelines()
      .then((list) => {
        const match = list.find((p) => p.envId === envId);
        if (match) setTenantName(match.tenantName);
      })
      .catch(() => undefined);
  }, [envId]);

  const overall = overallState(status, !!error);

  return (
    <>
      <Breadcrumbs separator="›" aria-label="breadcrumb" sx={{ marginBottom: "20px" }}>
        <Link onClick={() => navigate("/Pipelines")} sx={{ cursor: "pointer" }} color="#777">
          Pipelines
        </Link>
        <Typography color="#777">{tenantName}</Typography>
      </Breadcrumbs>
      <Card sx={{ backgroundColor: "#2A2B31", maxWidth: "1450px" }}>
        <CardContent sx={{ paddingBottom: "16px !important" }}>
          <Card sx={{ marginBottom: "16px" }}>
            <CardHeader
              title={tenantName}
              action={
                <Button startIcon={<RefreshIcon />} onClick={loadStatus} disabled={loading}>
                  Refresh
                </Button>
              }
            />
            <Divider />
            <CardContent sx={{ paddingBottom: "16px !important" }}>
              <Grid container spacing={2}>
                <Summary label="Environment" value={envId} />
                <Summary label="Tenant ID" value={status?.tenant_id ?? "—"} />
                <Summary
                  label="Status"
                  value={<Chip size="small" label={overall.label} color={overall.color} />}
                />
                <Summary
                  label="Services running"
                  value={
                    status
                      ? `${Object.values(status.services).filter((s) => s.running).length} / ${
                          Object.keys(status.services).length
                        }`
                      : "—"
                  }
                />
              </Grid>
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </CardContent>
          </Card>

          <Tabs
            value={pageTab}
            onChange={(_e, v) => setPageTab(v)}
            aria-label="pipeline-tabs"
            sx={{ marginBottom: "16px" }}
          >
            <Tab label="Services" />
            <Tab label="Maintenance" />
            <Tab label="Rule cache" />
            <Tab label="PagerDuty alerts" />
          </Tabs>
          <Box>
            {pageTab === 0 && (
              <ServicesTab envId={envId} status={status} loading={loading} reload={loadStatus} />
            )}
            {pageTab === 1 && <MaintenanceTab envId={envId} status={status} reloadStatus={loadStatus} />}
            {pageTab === 2 && <RuleCacheTab envId={envId} />}
            {pageTab === 3 && <PagerDutyTab envId={envId} />}
          </Box>
        </CardContent>
      </Card>
    </>
  );
};

export default PipelineDetails;
